import React, { useState } from 'react';
import { Edit2 } from 'lucide-react';

const TABS = ['Personal Information', 'Security', 'Activity Log'];

// This app has no "current user" endpoint - login only returns an opaque
// OAuth token (see authAction.js), and the sidebar/header already display
// this same static name/role/initials with no state binding. These fields
// mirror that existing pattern rather than inventing a different identity;
// wiring real data here needs a backend profile endpoint first.
const CURRENT_USER = {
  initials: 'KA',
  fullName: 'K. Anderson',
  role: 'Administrator',
  email: 'kanderson@sltravel.com',
  contactNumber: '+94 77 234 5678',
  memberSince: 'Jan 15, 2024',
  lastLogin: 'Aug 30, 2026, 9:42 AM',
  passwordLastChanged: '4 months ago',
};

const Field = ({ label, value }) => (
  <div>
    <label className="block mb-1.5 text-xs font-semibold text-slate-700">{label}</label>
    <input
      value={value}
      disabled
      className="w-full h-[42px] px-3.5 border border-slate-200 rounded-lg text-sm text-slate-500 bg-slate-50"
    />
  </div>
);

export default function Profile() {
  const [activeTab, setActiveTab] = useState('Personal Information');

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>
        <div className="mt-1 text-sm text-slate-500">
          Home <span className="mx-1.5 text-slate-400">/</span>
          <span className="font-semibold text-slate-700">Profile</span>
        </div>
      </div>

      {/* Profile header card */}
      <div className="flex items-center justify-between p-6 mb-4 bg-white border rounded-xl border-slate-200">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center flex-shrink-0 rounded-full w-18 h-18 bg-brand-blue" style={{ width: 72, height: 72 }}>
            <span className="text-2xl font-bold text-white">{CURRENT_USER.initials}</span>
          </div>
          <div>
            <div className="mb-1 text-lg font-bold text-slate-900">{CURRENT_USER.fullName}</div>
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center h-[22px] px-2.5 rounded-full text-xs font-semibold bg-brand-blue/10 text-brand-blue">
                {CURRENT_USER.role}
              </span>
              <span className="text-sm text-slate-500">{CURRENT_USER.email}</span>
            </div>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors bg-white border rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50">
          <Edit2 size={14} />
          Edit Profile
        </button>
      </div>

      {/* Tabs */}
      <div className="flex mb-4 border-b border-slate-200">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? 'text-brand-blue border-brand-blue'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab !== 'Personal Information' ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white border rounded-xl border-slate-200">
          <div className="mb-1 text-base font-semibold text-slate-900">{activeTab} coming soon</div>
          <p className="max-w-sm text-sm text-slate-500">
            This section isn't wired up to the backend yet.
          </p>
        </div>
      ) : (
        <>
          {/* Personal Information */}
          <div className="p-6 mb-4 bg-white border rounded-xl border-slate-200">
            <div className="mb-4 text-sm font-bold text-slate-900">Personal Information</div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Full Name" value={CURRENT_USER.fullName} />
              <Field label="Role" value={CURRENT_USER.role} />
              <Field label="Email Address" value={CURRENT_USER.email} />
              <Field label="Contact Number" value={CURRENT_USER.contactNumber} />
              <Field label="Member Since" value={CURRENT_USER.memberSince} />
              <Field label="Last Login" value={CURRENT_USER.lastLogin} />
            </div>
          </div>

          {/* Password */}
          <div className="flex items-center justify-between p-6 bg-white border rounded-xl border-slate-200">
            <div>
              <div className="text-sm font-bold text-slate-900">Password</div>
              <div className="mt-1 text-xs text-slate-500">Last changed {CURRENT_USER.passwordLastChanged}</div>
            </div>
            <button className="h-10 px-4 text-sm font-semibold text-white transition-colors rounded-lg bg-brand-blue hover:bg-brand-blue-dark">
              Change Password
            </button>
          </div>
        </>
      )}
    </div>
  );
}
